import { useTheme } from "@emotion/react";
import React, { useState, useEffect, useRef } from "react";
import { LightTooltip } from "../../../../kit/LightToolTip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningIcon from "@mui/icons-material/Warning";
import Radio from "@mui/material/Radio";
import VerticalDivider from "../../../../components/MUIComponents/VerticalDivider";
function RadioInput({
  label,
  type,
  radioList,
  value,
  isRequired = false,
  isDisabled = false,
  info = "",
  onChange,
  componentId,
  categoryId,
  description,
  isEditable = true,
  downgradePossible = true,
  warning = "",
  radioChipsCurrentValue = null, // New prop to get current radio chips value
}) {
  const theme = useTheme();
  
  // State management
  const [initialProp, setInitialProp] = useState(value);
  const [currentValue, setCurrentValue] = useState(value);
  
  // Update states when value prop changes
  useEffect(() => {
    // setInitialProp(value);
    setCurrentValue(value);
  }, [value]);
  
  // Refs to track if we've already processed the values to prevent infinite loops
  const hasProcessedYesRef = useRef(false);
  const hasProcessedNoRef = useRef(false);
  
  // Auto-update RadioChips component when value becomes "Yes" or "No"
  useEffect(() => {
    // Define mapping inside useEffect to avoid dependency issues
    const mapping = [
      {
        CategoryId: 4,
        radioComponentId: 7,
        radioChipsComponentId: 8,
        yesValue: "Small",
        noValue: null
      },
      {
        CategoryId: 4,
        radioComponentId: 10,
        radioChipsComponentId: 11,
        yesValue: "Small",
        noValue: null
      },
      // {
      //   CategoryId: 8,
      //   radioComponentId: 34,
      //   radioChipsComponentId: 35,
      //   yesValue: "Basic",
      //   noValue: null
      // },
    ];

    // Find the mapping entry that matches current categoryId and componentId
    const mappingEntry = mapping.find(
      item => item.CategoryId === categoryId && item.radioComponentId === componentId
    );

    if (!mappingEntry || !onChange) {
      return;
    }

    // Handle "Yes" scenario: only change if radio chips is currently null
    if (value === "Yes" && !hasProcessedYesRef.current) {
      // Only set radio chips to "Small" if it's currently null
      if (radioChipsCurrentValue === null || radioChipsCurrentValue === undefined) {
        hasProcessedYesRef.current = true;
        onChange(mappingEntry.CategoryId, mappingEntry.radioChipsComponentId, mappingEntry.yesValue);
      }
    }
    
    // Handle "No" scenario: always set to null regardless of current value
    else if (value === "No" && !hasProcessedNoRef.current) {
      hasProcessedNoRef.current = true;
      onChange(mappingEntry.CategoryId, mappingEntry.radioChipsComponentId, mappingEntry.noValue);
    }
    
    // Reset flags when value changes
    if (value === "Yes") {
      hasProcessedNoRef.current = false;
    } else if (value === "No") {
      hasProcessedYesRef.current = false;
    } else {
      hasProcessedYesRef.current = false;
      hasProcessedNoRef.current = false;
    }
  }, [value, categoryId, componentId, onChange]);
  
  // Logic to determine if radio buttons should be disabled
  const isYesDisabled = isDisabled; // Yes button is only disabled by general isDisabled prop
  const isNoDisabled = isDisabled || (initialProp === "Yes" && !downgradePossible);
  
  return (
    <div>
      <div
        style={{
          fontSize: theme.size[12],
          fontWeight: theme.fontWeight[500],
          color: theme.palette.rapidInfra.text,
        }}
      >
        {label}

        {isRequired && (
          <span
            style={{ color: theme.palette.rapidInfra.required, marginLeft: 5 }}
          >
            *
          </span>
        )}
        {info && (
          <LightTooltip
            title={info}
            placement="top"
            arrow
            sx={{
              "& .MuiTooltip-tooltip": {
                padding: "8px 12px", // Adjust padding if needed
              },
            }}
          >
            <InfoOutlinedIcon
              style={{
                fontSize: theme.size[14],
                color: theme.palette.rapidInfra.infoIcon,
                marginLeft: 5,
                cursor: "pointer",
              }}
            />
          </LightTooltip>
        )}
        {warning && (
          <LightTooltip
            title={warning}
            placement="top"
            arrow
            sx={{
              "& .MuiTooltip-tooltip": {
                padding: "8px 12px", // Adjust padding if needed
              },
            }}
          >
            <WarningIcon
              style={{
                fontSize: theme.size[14],
                color: "#FF9800", // Orange color for warning
                marginLeft: 5,
                cursor: "pointer",
              }}
            />
          </LightTooltip>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center" }}>
        <div
          style={{
            marginRight: "15px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Radio
            checked={currentValue === radioList[0]?.property_value}
            onChange={() => {
              setCurrentValue("Yes");
              onChange(categoryId, componentId, "Yes");
            }}
            disabled={isYesDisabled}
            value="Yes"
            name="radio-buttons"
            inputProps={{ "aria-label": "Yex" }}
            sx={{
              padding: "5px",
              color: theme.palette.secondary.main,
              "&.Mui-checked": {
                color: theme.palette.secondary.main,
              },
              // Styling for disabled state
              "&.Mui-disabled": {
                color: theme.palette.rapidInfra.disabledGrey, // Custom color when disabled
              },
              "&.Mui-disabled .MuiSvgIcon-root": {
                color: theme.palette.rapidInfra.disabledGrey, // Ensure icon is also grayed out when disabled
              },
            }}
          />
          <span
            style={{
              fontSize: theme.size[12],
              fontWeight: theme.fontWeight[400],
              color: isYesDisabled ? "#bdbdbd" : "#383838",
            }}
          >
            {radioList[0]?.property_value}
          </span>
        </div>

        <VerticalDivider />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginLeft: "10px",
          }}
        >
          <Radio
            checked={currentValue === radioList[1]?.property_value}
            onChange={() => {
              setCurrentValue("No");
              onChange(categoryId, componentId, "No");
            }}
            disabled={isNoDisabled}
            value="No"
            name="radio-buttons"
            inputProps={{ "aria-label": "No" }}
            sx={{
              padding: "5px",
              color: theme.palette.secondary.main,
              "&.Mui-checked": {
                color: theme.palette.secondary.main,
              },
              // Styling for disabled state
              "&.Mui-disabled": {
                color: theme.palette.rapidInfra.disabledGrey, // Custom color when disabled
              },
              "&.Mui-disabled .MuiSvgIcon-root": {
                color: theme.palette.rapidInfra.disabledGrey, // Ensure icon is also grayed out when disabled
              },
            }}
          />
          <span
            style={{
              fontSize: theme.size[12],
              fontWeight: theme.fontWeight[400],
              color: isNoDisabled ? "#bdbdbd" : "#383838",
            }}
          >
            {radioList[1]?.property_value}
          </span>
        </div>
      </div>
      {description !== "" && (
        <span
          style={{
            fontSize: theme.size[11],
            fontWeight: theme.fontWeight[500],
            color: theme.palette.secondary.main,
          }}
        >
          {description}
        </span>
      )}
    </div>
  );
}

export default RadioInput;
